import { Signal, computed } from '@angular/core';
import { IDisplaySlide, SlideType } from '@pcp/core/interfaces';
import { extractSlideTypeCategory, isNotNullOrUndefined } from '@pcp/core/util';
import { ITransferDisplaySlide } from '@pcp/streaming';
import { createBookpageDisplaySlide } from './util/create-bookpage-display-slide';

/**
 * Signal-based slide-identity reads for one preview-screen layer.
 *
 * Replaces the parametric `ActiveElementSelector` NgRx class (`fromPrimaryElement`,
 * `fromSecondaryElement`, `fromTertiaryElement`) that read from the deleted
 * active-element reducer. Inputs come from `ClassroomFacade` per-layer Signals
 * (`primarySlide` / `secondarySlide` / `tertiarySlide` + the matching
 * `*SlideIsAccessedViaHotspot` Signals); consumers `toObservable(...)` them
 * if they need Observable form.
 *
 * `IDisplaySlide` here is the read-side type the templates already expect — Y.Doc
 * stores the structurally-compatible `ITransferDisplaySlide`, so we cast at the
 * boundary. The transfer-vs-display difference is a string-vs-typed-enum on
 * `SlideType` plus a few imageVariants quirks; for the read-only Signal helpers
 * below those don't matter.
 */
export class LayerSignals {
    constructor(
        private readonly slideSignal: Signal<ITransferDisplaySlide | null>,
        private readonly hotspotSignal: Signal<boolean>
    ) {}

    readonly slide: Signal<IDisplaySlide | null> = computed(
        () => this.slideSignal() as unknown as IDisplaySlide | null
    );

    readonly slideId: Signal<number | undefined> = computed(() => this.slide()?.id);

    readonly guid: Signal<string | undefined> = computed(() => this.slide()?.guid);

    readonly slideType: Signal<SlideType | undefined> = computed(() => this.slide()?.type);

    readonly slideTypeCategory = computed(() => extractSlideTypeCategory(this.slide()));

    readonly slideIdWithType: Signal<{ id: number; type: SlideType } | undefined> = computed(() => {
        const s = this.slide();
        return s ? { id: s.id, type: s.type } : undefined;
    });

    readonly hotspots = computed(() => this.slide()?.hotspots);

    readonly isAccessedViaHotspot: Signal<boolean> = this.hotspotSignal;

    readonly favoritesDisabled: Signal<boolean> = computed(() => {
        const s = this.slide();
        if (s === null || s === undefined) return true;
        return (
            s.hideFavoriteButton === true ||
            s.type === SlideType.bookpage ||
            s.type === SlideType.module ||
            s.id < 1
        );
    });
}

/**
 * Bookpage Signals — composed from the primary slide (`bookpages` collection) and the
 * secondary slide (`bookpageNo`). Replaces the `fromBookpages.*` NgRx selectors.
 *
 * All bookpages logic is purely derived; no local state. The returned signals stay
 * coherent with the source primary/secondary slide Signals automatically.
 */
export class BookpageSignals {
    constructor(
        primarySlide: Signal<ITransferDisplaySlide | null>,
        secondarySlide: Signal<ITransferDisplaySlide | null>
    ) {
        this.availableBookpages = computed(
            () => (primarySlide() as unknown as IDisplaySlide | null)?.bookpages ?? []
        );

        this.isShowingBookpage = computed(() => {
            const secondary = secondarySlide() as unknown as IDisplaySlide | null;
            return (
                isNotNullOrUndefined(secondary) &&
                secondary.type === SlideType.bookpage &&
                secondary.bookpageNo !== undefined
            );
        });

        const activatedBookpage = computed(() => {
            const secondary = secondarySlide() as unknown as IDisplaySlide | null;
            const bookpageNo = secondary?.bookpageNo;
            if (bookpageNo === undefined) return undefined;
            return this.availableBookpages().find((bp) => bp.page === bookpageNo);
        });

        const activatedBookpageIdx = computed(() => {
            if (!this.isShowingBookpage()) return undefined;
            const bp = activatedBookpage();
            if (bp === undefined) return undefined;
            return this.availableBookpages().indexOf(bp);
        });

        this.previousBookpage = computed(() => {
            const idx = activatedBookpageIdx();
            const bookpages = this.availableBookpages();
            return idx !== undefined && Number.isFinite(idx) && idx >= 0
                ? bookpages[idx - 1]
                : undefined;
        });

        this.nextBookpage = computed(() => {
            const idx = activatedBookpageIdx();
            const bookpages = this.availableBookpages();
            return idx !== undefined && Number.isFinite(idx) && idx >= 0 && idx < bookpages.length
                ? bookpages[idx + 1]
                : undefined;
        });

        this.hasPreviousBookpage = computed(() => this.previousBookpage() !== undefined);
        this.hasNextBookpage = computed(() => this.nextBookpage() !== undefined);

        this.previousBookpageAsDisplaySlide = computed(() => {
            const bp = this.previousBookpage();
            return bp !== undefined ? createBookpageDisplaySlide(bp) : undefined;
        });

        this.nextBookpageAsDisplaySlide = computed(() => {
            const bp = this.nextBookpage();
            return bp !== undefined ? createBookpageDisplaySlide(bp) : undefined;
        });

        this.isBookpageToolActivateable = computed(() => this.availableBookpages().length > 0);
    }

    readonly availableBookpages;
    readonly isShowingBookpage;
    readonly previousBookpage;
    readonly nextBookpage;
    readonly hasPreviousBookpage;
    readonly hasNextBookpage;
    readonly previousBookpageAsDisplaySlide;
    readonly nextBookpageAsDisplaySlide;
    readonly isBookpageToolActivateable;
}
